/**
 * Recording session orchestration hook.
 *
 * Manages session IDs, chunk persistence + progressive upload,
 * draft creation on recording stop, and upload finalization.
 */

import { useRef, useCallback } from 'react'
import { useStore } from '@/stores'
import { useRecorder, type RecordedChunk } from '@/hooks/useRecorder'
import {
  enqueueRecordingChunkUpload,
  saveDraft,
  saveRecordingChunk,
} from '@/lib/db'
import { useToast } from '@/components/providers/ToastProvider'
import { newDraftSessionId } from '@/lib/ids'
import type { AudioConsentMetadata, DraftRecording } from '@/types'
import { useAuthStateSnapshot } from '@/lib/authStatus'
import { ensureAudioBlobType } from '@/lib/audio'
import { recordingStartedAt } from '@/lib/audio/recordingStart'
import { uploadChunkedDraft } from '@/lib/sync'
import { uploadChunk as progressiveUploadChunk, finalizeSession } from '@/lib/sync/chunkUploader'
import { dispatchUploadProgressEvent } from '@/lib/uploadEvents'
import { normalizeTaxonomySelection } from '@/lib/taxonomySelection'
import { ensurePersistentStorage } from '@/lib/storagePersist'
import { copy } from '@/copy/strings'

interface UseRecordingSessionOptions {
  onStreamChange?: (stream: MediaStream | null) => void
  onSaveFailure: () => void
  forcedStopPendingRef: React.MutableRefObject<boolean>
}

export function useRecordingSession(options: UseRecordingSessionOptions) {
  const sessionIdRef = useRef<string | null>(null)
  const sessionCreatedAtRef = useRef<string | null>(null)
  const lastStoppedSessionIdRef = useRef<string | null>(null)
  const chunkIdsRef = useRef<string[]>([])
  const pendingConsentRef = useRef<AudioConsentMetadata | null>(null)

  const setDraft = useStore((state) => state.setDraft)
  const { showToast } = useToast()
  const authState = useAuthStateSnapshot()

  const ensureSessionId = (): string => {
    if (!sessionIdRef.current) {
      sessionIdRef.current = newDraftSessionId()
    }
    return sessionIdRef.current
  }

  const getSessionId = () => sessionIdRef.current
  const getLastStoppedSessionId = () => lastStoppedSessionIdRef.current

  const resetSession = () => {
    sessionIdRef.current = null
    sessionCreatedAtRef.current = null
    chunkIdsRef.current = []
  }

  const recorder = useRecorder({
    onRecordingStart: (stream) => {
      const sessionId = ensureSessionId()
      sessionCreatedAtRef.current = new Date().toISOString()
      dispatchUploadProgressEvent({ type: 'finalize', phase: 'idle', sessionId })
      void ensurePersistentStorage()
      options.onStreamChange?.(stream)
      showToast('Recording started', 'success', 2000)
    },

    onChunkAvailable: async (chunk: RecordedChunk) => {
      try {
        const sessionId = ensureSessionId()
        const chunkId = `${sessionId}-chunk-${chunk.index}`
        const nowIso = new Date().toISOString()
        chunkIdsRef.current[chunk.index] = chunkId
        const typedChunk = ensureAudioBlobType(chunk.blob)
        await saveRecordingChunk({
          id: chunkId,
          sessionId,
          recordingSessionId: sessionId,
          index: chunk.index,
          chunkIndex: chunk.index,
          durationMs: chunk.durationMs,
          createdAt: chunk.createdAt,
          queuedAt: chunk.createdAt,
          enqueuedAt: nowIso,
          updatedAt: nowIso,
          blob: typedChunk,
          blobType: typedChunk.type,
          isFinal: chunk.isFinal,
          uploadStatus: 'pending',
          ackStatus: 'pending',
          acknowledgedAt: null,
          lastUploadAttemptAt: null,
          retryCount: 0,
          lastRetryAt: null,
        })
        await enqueueRecordingChunkUpload(chunkId)

        progressiveUploadChunk(
          sessionId,
          chunk.index,
          typedChunk,
          typedChunk.type || 'audio/webm',
          0,
          chunkId,
        )
      } catch (error) {
        console.error('Failed to persist chunk', error)
      }
    },

    onRecordingStop: async ({ blob, durationMs, chunks }) => {
      options.onStreamChange?.(null)

      // The recorder never delivered audio (seen on iOS Safari when the mic is
      // muted). There is nothing to save or upload, and finalizing would create
      // an empty submission on the server, so discard it and tell the user.
      if (chunks.length === 0) {
        console.warn('[recorder] stopped without capturing audio; discarding', sessionIdRef.current)
        showToast(copy.recordingNoAudioCaptured, 'error', 8000)
        // Nothing was persisted, so forced-stop recovery has nothing to salvage.
        options.forcedStopPendingRef.current = false
        resetSession()
        return
      }

      // Read the selection at stop time (not via stale render-scope closures) so
      // dropdown changes made while recording are reflected on the saved record.
      const { selectedAssetId, selectedIndicationId, selectedKiqIds, selectedInsightTags } =
        useStore.getState()
      const taxonomySelection = normalizeTaxonomySelection({
        assetId: selectedAssetId,
        indicationId: selectedIndicationId,
        kiqIds: selectedKiqIds,
      })

      const typedBlob = ensureAudioBlobType(blob)
      const draftId = ensureSessionId()
      lastStoppedSessionIdRef.current = draftId
      const chunkIds =
        chunkIdsRef.current.length > 0
          ? chunkIdsRef.current.slice(0, chunks.length || chunkIdsRef.current.length)
          : chunks.map((chunk) => `${draftId}-chunk-${chunk.index}`)
      const draft: DraftRecording = {
        id: draftId,
        userId: authState.userSub,
        assetId: taxonomySelection.assetId,
        indicationId: taxonomySelection.indicationId,
        kiqIds: taxonomySelection.kiqIds,
        insightTags: selectedInsightTags,
        blob: typedBlob,
        durationMs: durationMs,
        // When the recording started (UTC instant), not when it was stopped.
        createdAt:
          sessionCreatedAtRef.current ?? recordingStartedAt(chunks) ?? new Date().toISOString(),
        chunkSessionId: draftId,
        chunkIds,
        chunkCount: chunks.length,
        participants: pendingConsentRef.current?.participants,
        recordingWithoutParticipants: pendingConsentRef.current?.recordingWithoutParticipants,
        consentDescription: pendingConsentRef.current?.consentDescription,
        consentConfirmed: pendingConsentRef.current?.consentConfirmed,
      }

      try {
        await saveDraft(draft)
        setDraft(draft)
        showToast('Recording saved as draft', 'success')

        dispatchUploadProgressEvent({ type: 'finalize', phase: 'pending', sessionId: draft.id })

        finalizeSession(draftId, {
          submissionId: draftId,
          totalChunks: chunks.length,
          assetId: taxonomySelection.assetId,
          indicationId: taxonomySelection.indicationId,
          kiqIds: taxonomySelection.kiqIds,
          insightTags: selectedInsightTags,
          createdAt: draft.createdAt,
          audioMimeType: typedBlob.type || 'audio/webm',
          durationMs: durationMs,
          participants: draft.participants,
          recordingWithoutParticipants: draft.recordingWithoutParticipants,
          consentDescription: draft.consentDescription,
          consentConfirmed: draft.consentConfirmed,
        })
          .then(() => {
            dispatchUploadProgressEvent({
              type: 'finalize',
              phase: 'complete',
              sessionId: draft.id,
            })
          })
          .catch((error) => {
            console.log('Progressive finalize failed, falling back to chunked draft upload:', error)
            return uploadChunkedDraft(draft)
              .then((success) => {
                if (success) {
                  dispatchUploadProgressEvent({
                    type: 'finalize',
                    phase: 'complete',
                    sessionId: draft.id,
                  })
                }
              })
          })
          .catch((error) => {
            console.log('Background sync will handle this draft:', error)
          })

        window.dispatchEvent(new Event('recording-saved'))
      } catch (error) {
        console.error('Failed to save draft:', error)
        if (options.forcedStopPendingRef.current) {
          showToast(
            'Failed to save recording after returning to the app. Trying to recover and save your recording…',
            'error',
            5000,
          )
        } else {
          options.onSaveFailure()
        }
      } finally {
        resetSession()
      }
    },
  })

  const startWithConsent = useCallback(
    (consentMetadata: AudioConsentMetadata) => {
      pendingConsentRef.current = consentMetadata
      chunkIdsRef.current = []
      ensureSessionId()
      recorder.startRecording()
    },
    [recorder.startRecording],
  )

  return {
    ...recorder,
    startWithConsent,
    ensureSessionId,
    getSessionId,
    getLastStoppedSessionId,
    pendingConsentRef,
  }
}
